/**
 * TradeStack - Customers List Page
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import './Customers.css';

function Customers() {
  const { formatCurrency } = useConfig();
  const [searchParams, setSearchParams] = useSearchParams();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ total: 0 });

  const searchQuery = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page')) || 1;

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, page]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await api.getCustomers({
        search: searchQuery,
        limit: 20,
        offset: (page - 1) * 20
      });
      setCustomers(response.data.customers || []);
      setPagination({ total: response.data.total || 0 });
    } catch (err) {
      setError('Failed to load customers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const search = formData.get('search');
    const params = new URLSearchParams(searchParams);
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    params.delete('page');
    setSearchParams(params);
  };

  return (
    <div className="customers-page">
      <div className="page-header">
        <h1>Customers</h1>
        <Link to="/customers/new" className="btn btn-primary">+ Add Customer</Link>
      </div>

      {/* Search */}
      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            name="search"
            placeholder="Search customers..."
            defaultValue={searchQuery}
          />
          <button type="submit" className="btn btn-secondary">Search</button>
        </form>
      </div>

      {/* Customers List */}
      {loading ? (
        <div className="loading-state">Loading customers...</div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : customers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👤</div>
          <h3>No customers found</h3>
          <p>Add your first customer to get started</p>
          <Link to="/customers/new" className="btn btn-primary">Add Customer</Link>
        </div>
      ) : (
        <div className="customers-grid">
          {customers.map(customer => (
            <Link to={`/customers/${customer.id}`} key={customer.id} className="customer-card">
              <div className="customer-avatar">
                {(customer.first_name?.[0] || customer.company_name?.[0] || '?').toUpperCase()}
              </div>
              <div className="customer-info">
                <h3 className="customer-name">
                  {customer.company_name || `${customer.first_name} ${customer.last_name}`}
                </h3>
                {customer.company_name && customer.first_name && (
                  <span className="customer-contact">
                    {customer.first_name} {customer.last_name}
                  </span>
                )}
                {customer.email && (
                  <span className="customer-email">{customer.email}</span>
                )}
                {customer.phone && (
                  <span className="customer-phone">{customer.phone}</span>
                )}
              </div>
              <div className="customer-stats">
                <div className="stat">
                  <span className="stat-value">{customer.job_count || 0}</span>
                  <span className="stat-label">Jobs</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{formatCurrency(customer.total_revenue || 0)}</span>
                  <span className="stat-label">Revenue</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.total > 20 && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            disabled={page === 1}
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('page', page - 1);
              setSearchParams(params);
            }}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {page} of {Math.ceil(pagination.total / 20)}
          </span>
          <button
            className="btn btn-secondary"
            disabled={page >= Math.ceil(pagination.total / 20)}
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('page', page + 1);
              setSearchParams(params);
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default Customers;
